import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { FileText, Save, CheckCircle, Plus, X } from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

// Using centralized api

function PlayerFormPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSigned, setIsSigned] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    place_of_birth: '',
    nationalities: '',
    id_document: '',
    full_address: '',
    personal_phone: '',
    email: '',
    social_media: '',
    father_name: '',
    father_phone: '',
    mother_name: '',
    mother_phone: '',
    emergency_contact: '',
    emergency_phone: '',
    current_club: '',
    category_division: '',
    main_position: '',
    secondary_positions: '',
    dominant_foot: 'Derecha',
    height: '',
    weight: '',
    previous_clubs: ['', '', ''],
    national_team_callups: '',
    titles_achievements: '',
    relevant_injuries: '',
    career_objective: '',
    has_id_copy: false,
    has_photos: false,
    has_video_link: '',
    has_contract: false
  });

  useEffect(() => {
    if (user?.role === 'player') {
      fetchForm();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchForm = async () => {
    try {
      const response = await api.get('/player-form/my');
      setFormData({
        ...response.data,
        previous_clubs: response.data.previous_clubs.length > 0 ? response.data.previous_clubs : ['', '', '']
      });
      setIsSigned(!!response.data.signature_date);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching form:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const dataToSend = {
        ...formData,
        previous_clubs: formData.previous_clubs.filter(club => club.trim() !== '')
      };

      await api.post('/player-form', dataToSend);
      toast.success('Ficha guardada correctamente');
      fetchForm();
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Error al guardar ficha');
    } finally {
      setSaving(false);
    }
  };

  const handleSign = async () => {
    if (!window.confirm('¿Confirmas que todos los datos son correctos y deseas firmar esta ficha?')) {
      return;
    }

    try {
      await api.patch('/player-form/sign');
      toast.success('Ficha firmada exitosamente');
      setIsSigned(true);
      fetchForm();
    } catch (error) {
      console.error('Error signing form:', error);
      toast.error('Error al firmar ficha');
    }
  };

  const updatePreviousClub = (index, value) => {
    const newClubs = [...formData.previous_clubs];
    newClubs[index] = value;
    setFormData({ ...formData, previous_clubs: newClubs });
  };

  if (user?.role !== 'player') {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto">
          <Card className="bento-card">
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">La ficha de jugador solo está disponible para jugadores.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto" data-testid="player-form-page">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <FileText className="h-8 w-8 text-primary" />
                <h1 className="text-4xl font-bold tracking-tight">Ficha de Jugador</h1>
              </div>
              <p className="text-muted-foreground">Representando sueños</p>
            </div>
            {isSigned && (
              <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                <CheckCircle className="h-4 w-4 mr-2" />
                Firmada
              </Badge>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* DATOS PERSONALES */}
          <Card className="bento-card mb-6">
            <CardHeader>
              <CardTitle>1. DATOS PERSONALES</CardTitle>
              <CardDescription>Información básica del jugador</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="full_name">Nombre completo *</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="date_of_birth">Fecha de nacimiento *</Label>
                  <Input
                    id="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="place_of_birth">Lugar de nacimiento *</Label>
                  <Input
                    id="place_of_birth"
                    value={formData.place_of_birth}
                    onChange={(e) => setFormData({ ...formData, place_of_birth: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="nationalities">Nacionalidad(es) *</Label>
                  <Input
                    id="nationalities"
                    value={formData.nationalities}
                    onChange={(e) => setFormData({ ...formData, nationalities: e.target.value })}
                    placeholder="Ej: Española, Argentina"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="id_document">Documento de identidad / Pasaporte *</Label>
                  <Input
                    id="id_document"
                    value={formData.id_document}
                    onChange={(e) => setFormData({ ...formData, id_document: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="full_address">Dirección completa *</Label>
                  <Input
                    id="full_address"
                    value={formData.full_address}
                    onChange={(e) => setFormData({ ...formData, full_address: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="personal_phone">Teléfono personal *</Label>
                  <Input
                    id="personal_phone"
                    type="tel"
                    value={formData.personal_phone}
                    onChange={(e) => setFormData({ ...formData, personal_phone: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Correo electrónico *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="social_media">Red social principal (Instagram / X)</Label>
                  <Input
                    id="social_media"
                    value={formData.social_media}
                    onChange={(e) => setFormData({ ...formData, social_media: e.target.value })}
                    placeholder="@usuario"
                    className="mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* DATOS FAMILIARES */}
          <Card className="bento-card mb-6">
            <CardHeader>
              <CardTitle>2. DATOS FAMILIARES</CardTitle>
              <CardDescription>Información de contacto familiar</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="father_name">Nombre del padre</Label>
                  <Input
                    id="father_name"
                    value={formData.father_name}
                    onChange={(e) => setFormData({ ...formData, father_name: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="father_phone">Teléfono (padre)</Label>
                  <Input
                    id="father_phone"
                    type="tel"
                    value={formData.father_phone}
                    onChange={(e) => setFormData({ ...formData, father_phone: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="mother_name">Nombre de la madre</Label>
                  <Input
                    id="mother_name"
                    value={formData.mother_name}
                    onChange={(e) => setFormData({ ...formData, mother_name: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="mother_phone">Teléfono (madre)</Label>
                  <Input
                    id="mother_phone"
                    type="tel"
                    value={formData.mother_phone}
                    onChange={(e) => setFormData({ ...formData, mother_phone: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="emergency_contact">Contacto de emergencia (nombre)</Label>
                  <Input
                    id="emergency_contact"
                    value={formData.emergency_contact}
                    onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="emergency_phone">Teléfono de emergencia</Label>
                  <Input
                    id="emergency_phone"
                    type="tel"
                    value={formData.emergency_phone}
                    onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* DATOS DEPORTIVOS */}
          <Card className="bento-card mb-6">
            <CardHeader>
              <CardTitle>3. DATOS DEPORTIVOS</CardTitle>
              <CardDescription>Información deportiva del jugador</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="current_club">Club actual *</Label>
                  <Input
                    id="current_club"
                    value={formData.current_club}
                    onChange={(e) => setFormData({ ...formData, current_club: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="category_division">Categoría / división *</Label>
                  <Input
                    id="category_division"
                    value={formData.category_division}
                    onChange={(e) => setFormData({ ...formData, category_division: e.target.value })}
                    placeholder="Ej: Primera División, Juvenil A"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="main_position">Posición principal *</Label>
                  <Select
                    value={formData.main_position}
                    onValueChange={(value) => setFormData({ ...formData, main_position: value })}
                    required
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Selecciona posición" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Portero">Portero</SelectItem>
                      <SelectItem value="Defensa Central">Defensa Central</SelectItem>
                      <SelectItem value="Lateral Derecho">Lateral Derecho</SelectItem>
                      <SelectItem value="Lateral Izquierdo">Lateral Izquierdo</SelectItem>
                      <SelectItem value="Mediocentro Defensivo">Mediocentro Defensivo</SelectItem>
                      <SelectItem value="Mediocentro">Mediocentro</SelectItem>
                      <SelectItem value="Mediapunta">Mediapunta</SelectItem>
                      <SelectItem value="Extremo Derecho">Extremo Derecho</SelectItem>
                      <SelectItem value="Extremo Izquierdo">Extremo Izquierdo</SelectItem>
                      <SelectItem value="Delantero Centro">Delantero Centro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="secondary_positions">Posiciones secundarias</Label>
                  <Input
                    id="secondary_positions"
                    value={formData.secondary_positions}
                    onChange={(e) => setFormData({ ...formData, secondary_positions: e.target.value })}
                    placeholder="Ej: Lateral, Extremo"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="dominant_foot">Pierna dominante *</Label>
                  <Select
                    value={formData.dominant_foot}
                    onValueChange={(value) => setFormData({ ...formData, dominant_foot: value })}
                    required
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Derecha">Derecha</SelectItem>
                      <SelectItem value="Izquierda">Izquierda</SelectItem>
                      <SelectItem value="Ambas">Ambas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="height">Altura *</Label>
                  <Input
                    id="height"
                    value={formData.height}
                    onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                    placeholder="Ej: 1.85m"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="weight">Peso *</Label>
                  <Input
                    id="weight"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                    placeholder="Ej: 80kg"
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Clubes anteriores</Label>
                <div className="space-y-2 mt-2">
                  {formData.previous_clubs.map((club, index) => (
                    <Input
                      key={index}
                      value={club}
                      onChange={(e) => updatePreviousClub(index, e.target.value)}
                      placeholder={`Club anterior ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* TRAYECTORIA Y OBJETIVOS */}
          <Card className="bento-card mb-6">
            <CardHeader>
              <CardTitle>4. TRAYECTORIA Y OBJETIVOS</CardTitle>
              <CardDescription>Logros y metas del jugador</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="national_team_callups">¿Has sido convocado a selección? (categoría y año)</Label>
                <Input
                  id="national_team_callups"
                  value={formData.national_team_callups}
                  onChange={(e) => setFormData({ ...formData, national_team_callups: e.target.value })}
                  placeholder="Ej: Sub-20, 2024"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="titles_achievements">Títulos o logros importantes</Label>
                <Textarea
                  id="titles_achievements"
                  value={formData.titles_achievements}
                  onChange={(e) => setFormData({ ...formData, titles_achievements: e.target.value })}
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="relevant_injuries">Lesiones relevantes (tipo y fecha)</Label>
                <Textarea
                  id="relevant_injuries"
                  value={formData.relevant_injuries}
                  onChange={(e) => setFormData({ ...formData, relevant_injuries: e.target.value })}
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="career_objective">Objetivo principal en tu carrera *</Label>
                <Textarea
                  id="career_objective"
                  value={formData.career_objective}
                  onChange={(e) => setFormData({ ...formData, career_objective: e.target.value })}
                  required
                  rows={3}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* DOCUMENTOS Y MATERIALES */}
          <Card className="bento-card mb-6">
            <CardHeader>
              <CardTitle>5. DOCUMENTOS Y MATERIALES</CardTitle>
              <CardDescription>Marca los documentos que has proporcionado</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="has_id_copy"
                  checked={formData.has_id_copy}
                  onCheckedChange={(checked) => setFormData({ ...formData, has_id_copy: checked })}
                />
                <label htmlFor="has_id_copy" className="text-sm cursor-pointer">
                  Copia de documento o pasaporte
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="has_photos"
                  checked={formData.has_photos}
                  onCheckedChange={(checked) => setFormData({ ...formData, has_photos: checked })}
                />
                <label htmlFor="has_photos" className="text-sm cursor-pointer">
                  Foto tipo carnet y foto en acción de juego
                </label>
              </div>

              <div>
                <Label htmlFor="has_video_link">Link de video o highlights</Label>
                <Input
                  id="has_video_link"
                  type="url"
                  value={formData.has_video_link}
                  onChange={(e) => setFormData({ ...formData, has_video_link: e.target.value })}
                  placeholder="https://youtube.com/..."
                  className="mt-1"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="has_contract"
                  checked={formData.has_contract}
                  onCheckedChange={(checked) => setFormData({ ...formData, has_contract: checked })}
                />
                <label htmlFor="has_contract" className="text-sm cursor-pointer">
                  Último contrato o certificado de club (opcional)
                </label>
              </div>
            </CardContent>
          </Card>

          {/* ACTIONS */}
          <div className="flex items-center justify-between mb-8">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/dashboard')}
            >
              Cancelar
            </Button>
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={saving}
                data-testid="save-form-button"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Guardando...' : 'Guardar Ficha'}
              </Button>
              {!isSigned && (
                <Button
                  type="button"
                  onClick={handleSign}
                  variant="default"
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Firmar Ficha
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
}

export default PlayerFormPage;
